import { zodResolver } from '@hookform/resolvers/zod';
import { DialogTitle } from '@savvy-kit/components/dialog';
import { Text } from '@savvy-kit/foundations/text';
import { Controller, useForm } from 'react-hook-form';
import TrackedDialog from 'src/components/tracked/tracked-dialog';
import { formatCurrencyForSubmit } from 'src/utils/currency';
import { handleMutationError } from 'src/utils/handle-mutation-error';
import { z } from 'zod';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import { ClientPortalUpdateAccountBalanceMutation } from '../../api/accounts-mutations';
import { CLIENT_PORTAL_DIALOG_PANEL_CLASS } from '../common/constants';
import { AddAccountFormShell } from './add-account/add-account-form-shell';
import { AddAccountNumericField } from './add-account/add-account-numeric-field';
import { isNonNegativeAmount } from './add-account/add-account-schemas';
import {
  ACCOUNTS_UPDATE_BALANCE_DESCRIPTION,
  ACCOUNTS_UPDATE_BALANCE_ERROR_TOAST,
  ACCOUNTS_UPDATE_BALANCE_FIELD_LABEL,
  ACCOUNTS_UPDATE_BALANCE_SUBMIT_LABEL,
  ACCOUNTS_UPDATE_BALANCE_TITLE,
} from './constants';

const updateBalanceSchema = z.object({
  balance: z.string().trim().min(1, 'Enter an amount').refine(isNonNegativeAmount, 'Enter an amount of $0 or more'),
});

type UpdateBalanceFormValues = z.infer<typeof updateBalanceSchema>;

type AccountsUpdateBalanceDialogProps = {
  readonly accountId: string;
  readonly accountName: string;
  readonly currentBalance: string;
  readonly onUpdated: () => void;
  readonly onClose: () => void;
};

export function AccountsUpdateBalanceDialog({
  accountId,
  accountName,
  currentBalance,
  onUpdated,
  onClose,
}: AccountsUpdateBalanceDialogProps) {
  const [updateAccount, { loading }] = useMutationWithErrorLogging(ClientPortalUpdateAccountBalanceMutation);
  const { control, handleSubmit, formState } = useForm<UpdateBalanceFormValues>({
    resolver: zodResolver(updateBalanceSchema),
    defaultValues: { balance: currentBalance },
  });

  const submit = handleSubmit(async (values) => {
    try {
      await updateAccount({
        variables: { input: { id: accountId, currentBalance: formatCurrencyForSubmit(values.balance) ?? '' } },
      });
      onUpdated();
    } catch (error) {
      handleMutationError(error, ACCOUNTS_UPDATE_BALANCE_ERROR_TOAST);
    }
  });

  return (
    <TrackedDialog
      elementName="update-account-balance-dialog"
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
      className={CLIENT_PORTAL_DIALOG_PANEL_CLASS}
    >
      <DialogTitle className="sr-only">{ACCOUNTS_UPDATE_BALANCE_TITLE}</DialogTitle>
      <AddAccountFormShell
        title={ACCOUNTS_UPDATE_BALANCE_TITLE}
        onCancel={onClose}
        onSubmit={submit}
        submitLabel={ACCOUNTS_UPDATE_BALANCE_SUBMIT_LABEL}
        submitting={loading}
      >
        <Text variant="body-sm" className="sr-only md:not-sr-only">
          {ACCOUNTS_UPDATE_BALANCE_DESCRIPTION.replace('{name}', accountName)}
        </Text>
        <Controller
          control={control}
          name="balance"
          render={({ field }) => (
            <AddAccountNumericField
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              maxDecimals={2}
              id="client-portal-account-balance"
              label={ACCOUNTS_UPDATE_BALANCE_FIELD_LABEL}
              required
              autoFocus
              prependInputAddon="$"
              errorMessage={formState.errors.balance?.message}
            />
          )}
        />
      </AddAccountFormShell>
    </TrackedDialog>
  );
}
